import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, UserPlus, Save } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  crearUsuario, actualizarUsuario,
  type CrearUsuarioPayload, type ActualizarUsuarioPayload,
} from '@/services/api/usuarios.api';
import { getApiErrorMessage } from '@/services/api/client';
import type { UsuarioSistema, RolUsuario } from '@/types/dominio';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Usuario a editar; null/undefined = modo creación. */
  usuario?: UsuarioSistema | null;
}

const ROLES: { value: RolUsuario; label: string }[] = [
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'SOPORTE', label: 'Soporte' },
  { value: 'COMPRAS', label: 'Compras' },
  { value: 'GUARDIA', label: 'Guardia' },
  { value: 'PEOPLE', label: 'Gestión Humana' },
];

const ESTADO_INICIAL = {
  username: '',
  email: '',
  password: '',
  rol: '' as RolUsuario | '',
  activo: true,
};

export function UsuarioFormDialog({ open, onOpenChange, usuario }: Props) {
  const qc = useQueryClient();
  const esEdicion = !!usuario;
  const [form, setForm] = useState(ESTADO_INICIAL);

  // Sincroniza el formulario al abrir / cambiar de usuario.
  useEffect(() => {
    if (!open) return;
    if (usuario) {
      setForm({
        username: usuario.username,
        email: usuario.email ?? '',
        password: '',
        rol: usuario.rol,
        activo: usuario.activo,
      });
    } else {
      setForm(ESTADO_INICIAL);
    }
  }, [open, usuario]);

  const mutation = useMutation({
    mutationFn: () => {
      if (esEdicion && usuario) {
        const payload: ActualizarUsuarioPayload = {
          email: form.email.trim() || undefined,
          rol: form.rol || undefined,
          activo: form.activo,
        };
        if (form.password.trim()) payload.password = form.password;
        return actualizarUsuario(usuario.id_usuario, payload);
      }
      const payload: CrearUsuarioPayload = {
        username: form.username.trim(),
        email: form.email.trim() || undefined,
        password: form.password,
        rol: form.rol as RolUsuario,
      };
      return crearUsuario(payload);
    },
    onSuccess: (data) => {
      toast.success(esEdicion ? 'Usuario actualizado' : 'Usuario creado', {
        description: `${data.username} · ${ROLES.find((r) => r.value === data.rol)?.label ?? data.rol}`,
      });
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(esEdicion ? 'No se pudo actualizar' : 'No se pudo crear el usuario', {
        description: getApiErrorMessage(err),
      });
    },
  });

  const usernameValido = esEdicion || form.username.trim().length >= 3;
  const passwordValido = esEdicion || form.password.trim().length > 0;
  const rolValido = !!form.rol;
  const puedeGuardar = usernameValido && passwordValido && rolValido && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{esEdicion ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
          <DialogDescription>
            {esEdicion
              ? 'Modifica el rol, estado o restablece la contraseña.'
              : 'Crea una cuenta de acceso al sistema.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Username */}
          <div className="space-y-1.5">
            <Label htmlFor="username">Usuario</Label>
            <Input
              id="username"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              placeholder="nombre.apellido"
              disabled={esEdicion}
              autoComplete="off"
            />
            {esEdicion && (
              <p className="text-xs text-muted-foreground">El nombre de usuario no se puede cambiar.</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email (opcional)</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="usuario@gsh.com.co"
              autoComplete="off"
            />
          </div>

          {/* Rol + Estado */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="rol">Rol</Label>
              <Select value={form.rol} onValueChange={(v) => setForm((f) => ({ ...f, rol: v as RolUsuario }))}>
                <SelectTrigger id="rol"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {esEdicion && (
              <div className="space-y-1.5">
                <Label htmlFor="estado">Estado</Label>
                <Select
                  value={form.activo ? 'activo' : 'inactivo'}
                  onValueChange={(v) => setForm((f) => ({ ...f, activo: v === 'activo' }))}
                >
                  <SelectTrigger id="estado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="inactivo">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <Label htmlFor="password">
              {esEdicion ? 'Nueva contraseña (opcional)' : 'Contraseña'}
            </Label>
            <Input
              id="password"
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder={esEdicion ? 'Dejar en blanco para no cambiarla' : '••••••••'}
              autoComplete="new-password"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!puedeGuardar}>
            {mutation.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : esEdicion ? <Save className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            {esEdicion ? 'Guardar cambios' : 'Crear usuario'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
